import React, { useEffect, useRef } from 'react';

// ── Colour + icon config per event type ──────────────────────────────────────
const EVENT_CONFIG = {
  // Pipeline lifecycle
  node_start:   { bg: '#EFF2F5', color: '#0969DA', icon: '▶',  label: null },
  node_done:    { bg: '#DAFBE1', color: '#1A7F37', icon: '✅', label: null },
  state_update: null,   // skip — too noisy
  ping:         null,   // skip

  // Discovery
  app_scanned:  { bg: '#EFF2F5', color: '#0969DA', icon: '🔍', label: 'SCAN' },

  // Rationalization
  'node_done:rationalize': { bg: '#DAFBE1', color: '#1A7F37', icon: '🧠', label: 'AI' },

  // Supervisor
  supervisor_auto_decided: { bg: '#FFF8C5', color: '#9A6700', icon: '🤖', label: 'AI DECISION' },
  supervisor_summary:      { bg: '#EFF2F5', color: '#0969DA', icon: '🤖', label: 'ROUTING' },

  // Requirements
  requirements_generating: { bg: '#EFF2F5', color: '#57606A', icon: '📝', label: 'REQUIREMENTS' },
  requirements_ready:      { bg: '#DAFBE1', color: '#1A7F37', icon: '📝', label: 'REQUIREMENTS' },

  // Coding
  coding_start: { bg: '#EFF2F5', color: '#0969DA', icon: '💻', label: 'BUILD' },
  coding_done:  { bg: '#DAFBE1', color: '#1A7F37', icon: '💻', label: 'BUILD' },

  // Testing
  testing_start: { bg: '#EFF2F5', color: '#57606A', icon: '🧪', label: 'TEST' },
  testing_done:  { bg: null,      color: null,       icon: '🧪', label: 'TEST' },  // dynamic

  // Security
  security_start: { bg: '#EFF2F5', color: '#57606A', icon: '🔒', label: 'SECURITY' },
  security_done:  { bg: null,      color: null,       icon: '🔒', label: 'SECURITY' },  // dynamic

  // Quality gate
  quality_decision: { bg: null, color: null, icon: '🔬', label: 'QUALITY' },  // dynamic

  // HITL
  hitl_decisions:    { bg: '#FFF8C5', color: '#9A6700', icon: '⏸', label: 'REVIEW' },
  hitl_requirements: { bg: '#FFF8C5', color: '#9A6700', icon: '⏸', label: 'REVIEW' },

  // Retired
  app_retired: { bg: '#FFEBE9', color: '#CF222E', icon: '🗑️', label: 'RETIRED' },

  // IaC
  iac_start:    { bg: '#E0F7FA', color: '#0E7490', icon: '🏗️', label: 'CLOUD' },
  iac_node_done:{ bg: '#E0F7FA', color: '#0E7490', icon: '🏗️', label: 'CLOUD' },
  iac_complete: { bg: '#DAFBE1', color: '#1A7F37', icon: '🏗️', label: 'CLOUD' },

  // Retry / rerun
  rerun_start:      { bg: '#EFF2F5', color: '#0969DA', icon: '↺', label: 'RETRY' },
  edge_decision:    { bg: '#EFF2F5', color: '#57606A', icon: '→', label: 'FLOW' },

  // Pause/resume
  pipeline_paused:  { bg: '#FFF8C5', color: '#9A6700', icon: '⏸', label: 'PAUSED' },
  pipeline_resumed: { bg: '#DAFBE1', color: '#1A7F37', icon: '▶', label: 'RESUMED' },

  // Progress sub-steps (NEW)
  progress: { bg: '#F6F8FA', color: '#57606A', icon: '·', label: null },

  // Kiro CLI diagnostics
  kiro_cli_start:   { bg: '#EEF2FF', color: '#4F46E5', icon: '🤖', label: 'KIRO' },
  kiro_cli_found:   { bg: '#ECFDF5', color: '#059669', icon: '📂', label: 'KIRO' },
  kiro_cli_ok:      { bg: '#ECFDF5', color: '#059669', icon: '✅', label: 'KIRO' },
  kiro_cli_error:   { bg: '#FFEBE9', color: '#CF222E', icon: '❌', label: 'KIRO' },
  kiro_cli_timeout: { bg: '#FFF8C5', color: '#9A6700', icon: '⏱️', label: 'KIRO' },

  // Terminal
  complete: { bg: '#DAFBE1', color: '#1A7F37', icon: '🎉', label: 'DONE' },
  error:    { bg: '#FFEBE9', color: '#CF222E', icon: '❌', label: 'ERROR' },
};

// ── Human-readable message builder ───────────────────────────────────────────
function buildMessage(type, data) {
  switch (type) {
    case 'node_start': {
      const nodeLabels = {
        discovery: 'Portfolio Scan', rationalization: 'AI Assessment',
        supervisor: 'Smart Routing', hitl_decision: 'Your Decisions',
        requirements: 'Requirements Draft', coding: 'Code Generation',
        testing: 'Quality Testing', security: 'Security Review',
        quality_gate: 'Quality Check', iac: 'Cloud Setup', audit: 'Final Report',
      };
      const node = data.node || '';
      const label = nodeLabels[node] || node.replace(/_/g, ' ');
      return `Starting: ${label}${data.app ? ` — ${data.app}` : ''}`;
    }

    case 'node_done':
      if (data.node?.startsWith('rationalize_') && data.app) {
        return `${data.app} → ${data.recommendation} (risk score ${data.score?.toFixed(1)}/10)`;
      }
      return `Completed: ${(data.node || '').replace(/_/g, ' ')}`;

    case 'app_scanned':
      return `Scanned ${data.name} — ${data.language}/${data.framework}, ${data.loc?.toLocaleString()} lines of code, ${data.issues} issues found`;

    case 'supervisor_auto_decided':
      return `AI Decision — ${data.decision?.toUpperCase()}: ${data.app} — ${data.reasoning} (${(data.confidence*100).toFixed(0)}% confidence)`;

    case 'supervisor_summary':
      return `${data.auto_decided?.length || 0} apps decided automatically, ${data.hitl_needed?.length || 0} sent for your review. Confidence: ${(data.pipeline_confidence*100).toFixed(0)}%`;

    case 'requirements_generating':
      return `Drafting ${data.decision} requirements for ${data.app}…`;

    case 'requirements_ready':
      return `Requirements ready for ${data.app} — ${data.chars?.toLocaleString()} characters`;

    case 'coding_start':
      return `${data.retry ? `[Retry ${data.retry_count}] ` : ''}Building modernized code for ${data.app} (${data.decision})…`;

    case 'coding_done':
      return `Code ready for ${data.app} — ${data.loc} lines (application) + ${data.test_loc} lines (tests)`;

    case 'testing_start':
      return `Running automated tests for ${data.app}…`;

    case 'testing_done':
      return `${data.app}: ${data.passed} passed, ${data.failed} failed, ${data.coverage?.toFixed(0)}% coverage [${(data.status || '').toUpperCase()}]`;

    case 'security_start':
      return `Running security scan for ${data.app}…`;

    case 'security_done':
      return `${data.app}: ${data.high} high risk, ${data.medium} medium, ${data.low} low [${data.tool}]`;

    case 'quality_decision':
      return `Quality check [${data.app}]: ${data.gate_decision?.toUpperCase()} — ${data.reasoning}${data.retry_count > 0 ? ` (retry ${data.retry_count}/3)` : ''}`;

    case 'hitl_decisions':
      return `Paused — ${data.recommendations?.length || 0} applications awaiting your decision`;

    case 'hitl_requirements':
      return `Paused — requirements ready for your review (${data.requirements?.length || 0} applications)`;

    case 'app_retired':
      return `${data.name} → Retired — all downstream steps skipped`;

    case 'iac_start':
      return `Generating cloud infrastructure scripts — Terraform and CDK in parallel`;

    case 'iac_node_done':
      return `${data.node === 'terraform' ? 'Terraform infrastructure' : 'AWS CDK'} scripts generated — ${data.lines} lines`;

    case 'iac_complete':
      return `Cloud infrastructure scripts ready — Terraform + CDK complete`;

    case 'edge_decision':
      return `Flow: ${data.from} → ${data.to} — ${data.reason}`;

    case 'pipeline_paused':
      return `Paused${data.stage ? ` at: ${data.stage}` : ''}`;

    case 'pipeline_resumed':
      return `Resumed — continuing modernization`;

    case 'rerun_start':
      return `Retrying from: ${data.stage}`;

    case 'progress':
      return data.msg || '';

    case 'kiro_cli_start':
      return `kiro-cli [${data.mode}] → ${data.agent} ${data.cd ? `--cd ${data.cd}` : '(no --cd)'}`;

    case 'kiro_cli_found':
      return `Found ${data.app} at ${data.path} — kiro-cli has full file access`;

    case 'kiro_cli_ok':
      return `kiro-cli responded — ${data.chars?.toLocaleString()} chars from ${data.agent}`;

    case 'kiro_cli_error':
      return `kiro-cli failed [${data.agent}]: ${data.error}`;

    case 'kiro_cli_timeout':
      return `kiro-cli timed out after ${data.timeout}s [${data.agent}]`;

    case 'complete':
      return `Modernization complete — ${data.deployment_status}`;

    case 'error':
      return `Error: ${data.message}`;

    default:
      return null;
  }
}

// ── Dynamic styling for events with variable outcomes ────────────────────────
function getStyle(type, data) {
  const base = EVENT_CONFIG[type];
  if (!base) return null;

  // Dynamic colours for outcome-dependent events
  if (type === 'testing_done') {
    const ok = data.failed === 0 && data.passed > 0;
    return { bg: ok ? '#DAFBE1' : '#FFF8C5', color: ok ? '#1A7F37' : '#9A6700', icon: ok ? '✅' : '⚠️', label: 'TEST' };
  }
  if (type === 'security_done') {
    const ok = data.high === 0;
    return { bg: ok ? '#DAFBE1' : '#FFEBE9', color: ok ? '#1A7F37' : '#CF222E', icon: ok ? '✅' : '❌', label: 'SEC' };
  }
  if (type === 'quality_decision') {
    const map = {
      pass:             { bg: '#DAFBE1', color: '#1A7F37', icon: '✅' },
      retry:            { bg: '#EFF2F5', color: '#0969DA', icon: '↺' },
      warn_and_proceed: { bg: '#FFF8C5', color: '#9A6700', icon: '⚠️' },
      block:            { bg: '#FFEBE9', color: '#CF222E', icon: '❌' },
    };
    return { ...(map[data.gate_decision] || map.pass), label: 'QUALITY' };
  }
  return base;
}

// ── Single log entry ──────────────────────────────────────────────────────────
function LogEntry({ evt }) {
  const { type, data, ts } = evt;

  if (EVENT_CONFIG[type] === null) return null;  // explicitly skipped

  const style = getStyle(type, data);
  if (!style) return null;

  const msg = buildMessage(type, data);
  if (!msg) return null;

  const isProgress = type === 'progress';
  const isSubStep  = isProgress || type === 'edge_decision';

  return (
    <div style={{
      display: 'flex',
      gap: 8,
      padding: isSubStep ? '2px 8px 2px 20px' : '4px 8px',
      borderRadius: 4,
      background: style.bg || 'transparent',
      borderLeft: isSubStep ? '2px solid #D0D7DE' : 'none',
      alignItems: 'flex-start',
      lineHeight: 1.5,
    }}>
      {/* Timestamp */}
      <span style={{ color: '#8B949E', fontSize: 10, flexShrink: 0, paddingTop: 2, minWidth: 58, fontFamily: 'monospace' }}>
        {ts}
      </span>

      {/* Label badge */}
      {style.label && !isSubStep && (
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
          background: style.color + '22', color: style.color,
          flexShrink: 0, alignSelf: 'center', letterSpacing: '0.3px',
        }}>
          {style.label}
        </span>
      )}

      {/* Icon */}
      <span style={{ flexShrink: 0, fontSize: isSubStep ? 11 : 13 }}>{style.icon}</span>

      {/* App tag */}
      {data.app && data.app !== 'all' && (
        <span style={{
          fontSize: 10, fontWeight: 700, color: '#0969DA',
          background: '#DDF4FF', padding: '0 5px', borderRadius: 4,
          flexShrink: 0, alignSelf: 'center',
        }}>
          {data.app.replace('legacy-', '')}
        </span>
      )}

      {/* Message */}
      <span style={{
        color: isSubStep ? '#57606A' : style.color || '#1A1F2E',
        fontSize: isSubStep ? 11 : 12,
        fontFamily: isSubStep ? "'Fira Code', monospace" : 'inherit',
        wordBreak: 'break-word',
        flex: 1,
      }}>
        {msg}
      </span>
    </div>
  );
}

// ── Stage → event types mapping ───────────────────────────────────────────────
const KIRO_EVENTS = ['kiro_cli_start', 'kiro_cli_found', 'kiro_cli_ok', 'kiro_cli_error', 'kiro_cli_timeout'];

const STAGE_EVENTS = {
  discovery:       ['app_scanned', 'node_start', 'node_done', 'progress', ...KIRO_EVENTS],
  rationalization: ['node_start', 'node_done', 'progress', ...KIRO_EVENTS],
  supervisor:      ['node_start', 'node_done', 'supervisor_auto_decided', 'supervisor_summary', 'progress', 'edge_decision'],
  hitl_decision:   ['hitl_decisions', 'app_retired', 'node_done'],
  requirements:    ['requirements_generating', 'requirements_ready', 'hitl_requirements', 'node_start', 'node_done', 'progress', ...KIRO_EVENTS],
  coding:          ['coding_start', 'coding_done', 'node_start', 'node_done', 'progress', ...KIRO_EVENTS],
  testing:         ['testing_start', 'testing_done', 'node_start', 'node_done', 'progress'],
  security:        ['security_start', 'security_done', 'node_start', 'node_done', 'progress'],
  quality_gate:    ['quality_decision', 'node_start', 'node_done', 'edge_decision', 'progress'],
  iac:             ['iac_start', 'iac_node_done', 'iac_complete', 'node_start', 'node_done', 'progress', ...KIRO_EVENTS],
  audit:           ['complete', 'node_start', 'node_done'],
};

function matchesStage(evt, stage) {
  const { type, data } = evt;
  const allowed = STAGE_EVENTS[stage];
  if (!allowed) return true;
  if (!allowed.includes(type)) return false;
  if (type === 'node_start' || type === 'node_done') {
    const node = data.node || '';
    if (stage === 'rationalization') return node === 'rationalization' || node.startsWith('rationalize_');
    if (stage === 'testing')  return node === 'testing'  || node.startsWith('test_');
    if (stage === 'security') return node === 'security' || node.startsWith('security_');
    return node === stage || node.startsWith(stage);
  }
  if (type === 'progress') return data.stage === stage;
  return true;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LiveLog({ entries, selectedStage, onClearFilter }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!selectedStage) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [entries, selectedStage]);

  // Filter out state_update and ping before rendering, then apply stage filter
  const base    = entries.filter(e => e.type !== 'state_update' && e.type !== 'ping');
  const visible = selectedStage ? base.filter(e => matchesStage(e, selectedStage)) : base;

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header">
        <span className="card-title-accent">📡</span> Live Activity
        {selectedStage && (
          <span style={{
            marginLeft: 8, fontSize: 11, fontWeight: 700,
            background: '#DDF4FF', color: '#0969DA',
            padding: '1px 8px', borderRadius: 8,
          }}>
            {selectedStage}
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--txt2)', fontWeight: 400, display: 'flex', alignItems: 'center', gap: 8 }}>
          {selectedStage && (
            <button onClick={onClearFilter} style={{
              fontSize: 10, padding: '1px 7px', borderRadius: 6, cursor: 'pointer',
              background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--txt2)',
            }}>
              ✕ show all
            </button>
          )}
          {visible.length} / {base.length} events
        </span>
      </div>
      <div className="live-log">
        {visible.length === 0 && (
          <span style={{ color: 'var(--txt2)', fontStyle: 'italic', fontSize: 12 }}>
            {selectedStage ? `No activity for: ${selectedStage}` : 'Waiting for modernization to start…'}
          </span>
        )}
        {visible.map((evt, i) => (
          <LogEntry key={evt.id || i} evt={evt} />
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
